import {
    IAddonsPassenger,
    IAddonsPassengerLeg,
    IAlreadyAddonsForPassenger,
    IAlreadyAddonsLegPassengerAddons
} from "../../Orchestrator/Types/Interface/IAddOns";
import { AncillaryStatusEnum } from "../../Orchestrator/Types/Enums/globalDefaultEnum";

// TODO: CLarify the order and priority of passenger types 
// for displaying addons in the UI, and use that for sorting
// passengers with addons in the addons modal and checkout
// page. For now, using a hardcoded priority list based 
// on common passenger types.
const PASSENGER_TYPE_PRIORITY: Record<string, number> = {
    adults: 1,
    seniorcitizens: 2,
    students: 3,
    children: 4,
    reducedMobility: 5,
    wheelchair: 6,
    infants: 7,
};

export const sortAddonsPassengersByType = (
    passengers: IAddonsPassenger[]
): IAddonsPassenger[] => {
    return [...passengers].sort((a, b) => {
        const aRank = PASSENGER_TYPE_PRIORITY[a.passengerType || ""] ?? 99;
        const bRank = PASSENGER_TYPE_PRIORITY[b.passengerType || ""] ?? 99;
        return aRank - bRank;
    });
};

/**
 * Function to check if any addons have been changed in manage addons flow
 * @param isManageSeating - Whether we're in manage addons flow
 * @param selectedAddonsLegPassenger - Current selected addons
 * @param alreadyAddonsLegPassenger - Original addons from booking
 * @returns boolean indicating if addons have changed or not.
 */
export const hasAddonsChanged = (
    isManageSeating: boolean,
    selectedAddonsLegPassenger: IAddonsPassengerLeg[],
    alreadyAddonsLegPassenger: IAlreadyAddonsLegPassengerAddons
): boolean => {
    let allOutboundAddons: IAlreadyAddonsForPassenger[] = [];
    let allInboundAddons: IAlreadyAddonsForPassenger[] = [];
    if (alreadyAddonsLegPassenger["outbound"]) {
        allOutboundAddons = [...(alreadyAddonsLegPassenger["outbound"] || [])];
    }
    if (alreadyAddonsLegPassenger["inbound"]) {
        allInboundAddons = [...(alreadyAddonsLegPassenger["inbound"] || [])];
    }
    let allWayAddons = [...allOutboundAddons, ...allInboundAddons]
    if (allWayAddons.length > 0) {
        const allLegId = selectedAddonsLegPassenger.map(d => d.legId)
        allWayAddons = allWayAddons.filter(d => allLegId.includes(d?.legId || ""))
    }

    if (!isManageSeating) {
        return false;
    }
    // return true

    // Flatten selected structure into the same shape as alreadyAddonsLegPassenger for comparison
    const selectedFlat = selectedAddonsLegPassenger.flatMap((leg: IAddonsPassengerLeg) =>
        leg.addonsPassenger.flatMap((p: IAddonsPassenger) =>
            Object.entries(p.addonItem).map(([category, addon]) => ({
                category,
                addonName: addon?.label || "",
                passengerId: p.passengerId,
                passengerName: p.passengerName,
                passengerRef: p.passengerRef,
                type: addon?.type || "",
                price: addon?.price || 0,
                stopCode: addon?.coveredLegId || "",
                legId: leg.legId,
            }))
        )
    );

    const sortFn = (a: any, b: any) =>
        a.legId.localeCompare(b.legId) ||
        a.passengerId.localeCompare(b.passengerId) ||
        a.category.localeCompare(b.category);

    const sortedAlready = [...allWayAddons].sort(sortFn);
    const sortedSelected = [...selectedFlat].sort(sortFn);

    if (sortedAlready.length !== sortedSelected.length) return true;

    for (let i = 0; i < sortedAlready.length; i++) {
        const a = sortedAlready[i];
        const b = sortedSelected[i];

        if (
            a.legId !== b.legId ||
            a.passengerId !== b.passengerId ||
            a.category !== b.category ||
            a.addonName !== b.addonName ||
            a.type !== b.type ||
            a.price !== b.price
        ) {
            return true;
        }
    }

    return false;
};

/**
 * Build the canonical addon fingerprint used by the change-date carry-over
 * snapshot. The format `${passengerRef}|${productCode}|${stopCode}` is
 * stable across the old→new offer boundary (legIds change between the old
 * and new prebooked offers but the stopCode OD-pair survives), so the
 * snapshot written by `SyncAlreadyAddonsAction` can be matched against the
 * picks rendered in `ManageSeatingAndAddons` without further translation.
 *
 * Every read and write of the fingerprint must go through this helper —
 * inlining the template literal is a copy-paste hazard if the format ever
 * needs to gain a new field.
 */
export const addonFingerprint = (
    passengerRef: string,
    productCode: string,
    stopCode: string,
): string => `${passengerRef}|${productCode}|${stopCode}`;

/**
 * Ancillary statuses that appear on a passenger's ticket. A ticket must only
 * show addons the passenger actually holds — PREBOOKED (unpaid picks),
 * REFUNDED and EXCHANGE_ONGOING items are excluded by design.
 */
const TICKET_VISIBLE_ANCILLARY_STATUSES: ReadonlySet<AncillaryStatusEnum> = new Set([
    AncillaryStatusEnum.FULFILLED,
    AncillaryStatusEnum.CONFIRMED,
]);

/**
 * Rows from `alreadyAddonsLegPassengerAtom` to render on the ticket of one
 * passenger for one leg (TicketsModal). Flattens the direction buckets and
 * filters by legId + passengerId + ticket-visible status.
 */
export const getTicketAddonsForPassenger = (
    alreadyAddonsLegPassenger: IAlreadyAddonsLegPassengerAddons | undefined,
    legId: string | undefined,
    passengerId: string | undefined,
): IAlreadyAddonsForPassenger[] => {
    if (!alreadyAddonsLegPassenger || !legId || !passengerId) return [];
    return Object.values(alreadyAddonsLegPassenger)
        .flat()
        .filter(
            (addon) =>
                addon.legId === legId &&
                addon.passengerId === passengerId &&
                !!addon.status &&
                TICKET_VISIBLE_ANCILLARY_STATUSES.has(addon.status),
        );
};
